import { useLazyQuery, useMutation } from "@apollo/client";
import Delete from "@mui/icons-material/Delete";
import {
  Box,
  CircularProgress,
  DialogContentText,
  GridLegacy as Grid,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  GridActionsCellItem,
  GridColDef,
  GridRowParams,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  ConfirmDialog,
  ResourceInfoDisplay,
} from "components/ConfirmDialog";
import { DataGridWithDefaultSort } from "components/DataGridWithDefaultSort";
import { Dialog } from "components/Dialog";
import CopyableTypography from "components/Typography/CopyableTypography";
import { gql } from "generated-graphql";
import { JobLog, JobStepsDone } from "generated-graphql/graphql";
import { compact, orderBy, sortBy } from "lodash-es";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  prettyPrintDateTime,
  prettyPrintDateTimeWithSeconds,
  relativeLogTime,
} from "util/dates";
import { GET_FACILITY_DETAILS } from "./useFacilityDetails";
import { GET_TIERII_REPORT, useReportDetails } from "./useReportDetails";

const DELETE_JOB_STEPS_DONE = gql(`
  mutation DeleteJobStepsDone($id: ID!) {
    deleteJobStepsDone(id: $id) {
      id
      name
    }
  }
`);

const GET_CHEMICALS_BY_IDS = gql(`
  query GetChemicalsForStepDeletion($ids: [ID!]!) {
    chemicalsByIds(ids: $ids) {
      id
      name
    }
  }
`);

export function WebAutomationJobInfo() {
  const theme = useTheme();
  const { data } = useReportDetails();
  const jobs = orderBy(
    data?.tierIIReport.facility.jobs ?? [],
    ["createdAt"],
    ["desc"]
  );

  const [selectedJob, setSelectedJob] = useState(jobs?.[0]);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: "include",
      ids: new Set(selectedJob?.id),
    });

  const selectedJobId = selectedJob?.id;

  // Sync selectedJob with latest data when jobs array updates
  useEffect(() => {
    if (selectedJobId && jobs.length > 0) {
      const updatedJob = jobs.find((j) => j.id === selectedJobId);
      if (updatedJob) {
        setSelectedJob(updatedJob);
      }
    }
  }, [jobs, selectedJobId]);

  const logs = sortBy(compact(selectedJob?.logs), (log) => log.createdAt);
  const stepsDone = compact(selectedJob?.stepsDone);
  const screenshotSrcs = compact(
    logs.map(
      (log) =>
        log.screenshotUrl && {
          src: log.screenshotUrl,
          createdAt: log.createdAt,
        }
    )
  );

  const handleImageClick = useCallback(
    (src: string) => window.open(src, "_blank"),
    []
  );
  const handleRowClick = (params: GridRowParams) => {
    setSelectedJob(params.row);
    setRowSelectionModel({ ids: new Set(params.row.id), type: "include" });
  };

  // Delete job step functionality
  const alerts = useAlerts();
  const [stepToDelete, setStepToDelete] = useState<JobStepsDone | null>(null);

  const [deleteJobStep, { loading: deleteLoading }] = useMutation(
    DELETE_JOB_STEPS_DONE,
    {
      refetchQueries: [GET_TIERII_REPORT, GET_FACILITY_DETAILS],
      awaitRefetchQueries: true,
      onCompleted: () => {
        alerts.success("Job step deleted successfully");
        setStepToDelete(null);
      },
      onError: (error) => {
        alerts.error(`Failed to delete job step: ${error.message}`);
      },
    }
  );

  const [fetchChemical, { data: chemicalData, loading: chemicalLoading }] =
    useLazyQuery(GET_CHEMICALS_BY_IDS, {
      fetchPolicy: "network-only",
    });

  const extractChemicalId = useCallback(
    (name: string | null | undefined): string | null => {
      if (!name) {
        return null;
      }
      const match = name.match(/^chemical-([0-9a-fA-F-]{36})$/);
      return match ? match[1] : null;
    },
    []
  );

  useEffect(() => {
    if (stepToDelete) {
      const chemicalId = extractChemicalId(stepToDelete.name);
      if (chemicalId) {
        fetchChemical({ variables: { ids: [chemicalId] } });
      }
    }
  }, [stepToDelete, fetchChemical, extractChemicalId]);

  const handleDeleteConfirm = useCallback(() => {
    if (stepToDelete) {
      deleteJobStep({ variables: { id: stepToDelete.id } });
    }
  }, [stepToDelete, deleteJobStep]);

  const handleDeleteClick = useCallback((step: JobStepsDone) => {
    setStepToDelete(step);
  }, []);

  const stepsDoneColumns: GridColDef<JobStepsDone>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 0.75,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "",
        width: 60,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        getActions: (params) => [
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label="Delete"
            onClick={() => handleDeleteClick(params.row)}
          />,
        ],
      },
    ],
    [handleDeleteClick]
  );

  return (
    <Grid
      item
      container
      sx={{
        paddingTop: theme.spacing(2),
        backgroundColor: theme.palette.background.paper,
        borderRadius: 1,
      }}
    >
      <Grid
        container
        spacing={theme.spacing(2)}
        padding={theme.spacing(2)}
        paddingTop={0}
        // TODO: This is a bad hack to deal with large pages of logs. Ideally we
        // would pull these into a paginated list of logs
        minHeight={"500px"}
      >
        <Grid item xs={12}>
          <Typography paddingLeft={theme.spacing(1)} variant="h6">
            Jobs
          </Typography>
          <DataGridWithDefaultSort
            autoHeight
            hideFooter
            rows={jobs}
            sortModel={[{ field: "createdAt", sort: "desc" }]}
            rowSelectionModel={rowSelectionModel}
            onRowClick={handleRowClick}
            columns={[
              {
                field: "id",
                headerName: "ID",
                width: 250,
                renderCell: ({ row }) => (
                  <CopyableTypography variant="body2" text={row.id} />
                ),
              },
              { field: "action", headerName: "Action", width: 160 },
              { field: "status", headerName: "Status" },
              {
                field: "createdAt",
                headerName: "Created At",
                width: 160,
                renderCell: ({ row }) => (
                  <Tooltip title={prettyPrintDateTime(row.createdAt)}>
                    <span>{relativeLogTime(row.createdAt)}</span>
                  </Tooltip>
                ),
              },
            ]}
          />
        </Grid>

        <Grid item xs={12} mb={theme.spacing(3)}>
          <Typography paddingLeft={theme.spacing(1)} variant="h6">
            Logs for {selectedJob && selectedJob.id}
          </Typography>
          <DataGridWithDefaultSort
            sx={{ maxHeight: "800px" }}
            hideFooter
            sortModel={[{ field: "createdAt", sort: "desc" }]}
            columns={logColumns}
            rows={logs}
          />
        </Grid>

        {screenshotSrcs.length > 0 && (
          <Grid item xs={12}>
            <Typography paddingLeft={theme.spacing(1)} variant="h6">
              Error Screenshots
            </Typography>

            <Grid item container spacing={1}>
              {screenshotSrcs.map(({ createdAt, src }) => (
                <Grid
                  item
                  key={src}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <img
                    style={{
                      width: 200,
                      height: 200,
                      borderRadius: 4,
                      marginTop: theme.spacing(2),
                      marginLeft: theme.spacing(1),
                      marginRight: theme.spacing(2),
                    }}
                    src={src}
                    alt={`Geppetto error screenshot ${src}`}
                    onClick={() => handleImageClick(src)}
                  />
                  <Typography
                    variant="caption"
                    style={{ marginTop: theme.spacing(1), textAlign: "center" }}
                  >
                    {prettyPrintDateTime(createdAt)}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}

        <Grid item xs={12}>
          <Typography paddingLeft={theme.spacing(1)} variant="h6">
            Steps Done
          </Typography>
          <DataGridWithDefaultSort
            autoHeight
            hideFooter
            sortModel={[{ field: "createdAt", sort: "desc" }]}
            columns={stepsDoneColumns}
            rows={stepsDone}
          />
        </Grid>
      </Grid>

      <ConfirmDialog
        open={!!stepToDelete}
        header="Delete Job Step"
        onClose={() => setStepToDelete(null)}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        confirmText="Delete"
        cancelText="Cancel"
      >
        <DialogContentText sx={{ mb: 2 }}>
          Are you sure you want to delete this job step?
        </DialogContentText>

        {stepToDelete && (
          <Box sx={{ mb: 2 }}>
            <ResourceInfoDisplay
              resourceType="Step Name"
              resourceName={stepToDelete.name}
            />

            {extractChemicalId(stepToDelete.name) && (
              <>
                {chemicalLoading ? (
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}
                  >
                    <CircularProgress size={16} />
                    <DialogContentText>
                      Loading chemical name...
                    </DialogContentText>
                  </Box>
                ) : chemicalData?.chemicalsByIds?.[0] ? (
                  <ResourceInfoDisplay
                    resourceType="Chemical Name"
                    resourceName={chemicalData.chemicalsByIds[0].name}
                  />
                ) : null}
              </>
            )}
          </Box>
        )}
      </ConfirmDialog>
    </Grid>
  );
}

const logColumns: GridColDef<JobLog>[] = [
  {
    field: "status",
    headerName: "Status",
    width: 200,
  },
  {
    field: "createdAt",
    headerName: "Logged At",
    width: 150,
    renderCell: ({ row }) => (
      <span>{prettyPrintDateTimeWithSeconds(row.createdAt)}</span>
    ),
  },
  {
    field: "errorMessage",
    headerName: "Message",
    flex: 0.5,
    renderCell: ({ row }) => <MessageCell message={row.errorMessage} />,
  },
  // TODO: maybe this can be a direct link to the Cloudwatch console
  // {
  //   field: "cloudWatchLogStream",
  //   headerName: "CloudWatch Log Stream",
  //   width: 260,
  //   renderCell: ({ row }) => (
  //     <CopyableTypography variant="body2" text={row.cloudWatchLogStream} />
  //   ),
  // },
];

const MessageCell = ({ message }: { message?: string | null }) => {
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const openDialog = () => setMessageDialogOpen(true);
  const closeDialog = () => setMessageDialogOpen(false);
  return (
    <>
      <Typography onClick={openDialog} variant="body2">
        {message}
      </Typography>

      <Dialog
        open={messageDialogOpen}
        onClose={closeDialog}
        title="Log Message"
        noPrimaryAction
      >
        <CopyableTypography variant="body2" text={message} />
      </Dialog>
    </>
  );
};
